import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, XCircle, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { GarageSelect, OptionSelect } from "@/components/GeneralFields";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { base: "", driver: "", category: "", chassis_id: null, engine_id: null };

const FileRow = ({ f, i }) => (
  <li data-testid={`quick-import-file-${i}`} className="flex items-center gap-2 px-3 py-1.5 font-mono text-xs border-b border-[#282D3D] last:border-0">
    {f.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" /> : f.status === "error" ? <XCircle className="w-4 h-4 text-[#FF3B30] shrink-0" />
      : f.status === "busy" ? <Loader2 className="w-4 h-4 text-[#FFB800] animate-spin shrink-0" /> : <FileUp className="w-4 h-4 text-slate-500 shrink-0" />}
    <span className="truncate flex-1 text-slate-300">{f.file.name}</span>
    {f.status === "busy" && <span className="text-[#FFB800]">{f.progress}%</span>}
    {f.name && <span data-testid={`quick-import-file-name-${i}`} className="text-[#10B981] font-bold">{f.name}</span>}
    {f.error && <span className="text-[#FF3B30] truncate max-w-[180px]" title={f.error}>{f.error}</span>}
  </li>
);

async function importOne(eventId, form, f, onProgress) {
  const { data: s } = await api.post(`/events/${eventId}/sessions`, {
    name: `${form.base} (import)`, driver: form.driver, category: form.category, chassis_id: form.chassis_id, engine_id: form.engine_id,
  });
  const fd = new FormData();
  fd.append("file", f.file);
  try {
    await api.post(`/sessions/${s.id}/telemetry`, fd, { params: { stint: 1 }, onUploadProgress: (ev) => onProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0) });
  } catch (err) {
    await api.delete(`/sessions/${s.id}`);
    throw err;
  }
  return s.id;
}

export const QuickImportDialog = ({ open, onOpenChange, event, garage, onDone }) => {
  const { t } = useT();
  const hideCat = useSettings().settings.hide_categories;
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, driver: event.drivers?.length === 1 ? event.drivers[0] : "", category: event.categories?.length === 1 ? event.categories[0] : "" });
      setFiles([]);
    }
  }, [open, event.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const patch = (i, p) => setFiles((fs) => fs.map((f, k) => (k === i ? { ...f, ...p } : f)));

  const run = async () => {
    setRunning(true);
    const ids = {};
    for (let i = 0; i < files.length; i++) {
      patch(i, { status: "busy", progress: 0, error: null });
      try {
        ids[i] = await importOne(event.id, form, files[i], (p) => patch(i, { progress: p }));
        patch(i, { status: "done" });
      } catch (err) {
        patch(i, { status: "error", error: formatApiError(err) });
      }
    }
    const done = Object.values(ids);
    if (done.length) {
      const { data } = await api.post(`/events/${event.id}/sessions/renumber`, { base: form.base, session_ids: done });
      const byId = Object.fromEntries(data.map((d) => [d.id, d.name]));
      Object.entries(ids).forEach(([i, id]) => patch(Number(i), { name: byId[id] }));
      toast.success(`${done.length} ${t("qi_created")}`);
      onDone();
    }
    setRunning(false);
  };

  const canRun = form.base.trim() && files.length && !running && files.some((f) => f.status !== "done");
  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent data-testid="quick-import-dialog" className="bg-[#12141C] border-[#282D3D] text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2"><Zap className="w-5 h-5 text-[#FFB800]" /> {t("qi_title")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label={t("qi_base")}>
              <Input data-testid="quick-import-base-input" value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })} placeholder="Prove" className={fieldInputCls} />
            </Field>
            <p className="mt-1 font-mono text-[10px] text-slate-500">{t("qi_base_hint").replaceAll("{n}", form.base || "Prove")}</p>
          </div>
          <OptionSelect label={t("driver")} testid="quick-import-driver-select" options={event.drivers || []} value={form.driver} onChange={(v) => setForm({ ...form, driver: v })} />
          {!hideCat ? <OptionSelect label={t("category")} testid="quick-import-category-select" options={event.categories || []} value={form.category} onChange={(v) => setForm({ ...form, category: v })} /> : <span />}
          <GarageSelect label={t("chassis")} testid="quick-import-chassis-select" items={garage.chassis} value={form.chassis_id} onChange={(v) => setForm({ ...form, chassis_id: v })} />
          <GarageSelect label={t("engine")} testid="quick-import-engine-select" items={garage.engines} value={form.engine_id} onChange={(v) => setForm({ ...form, engine_id: v })} />
        </div>
        <input ref={ref} type="file" multiple accept=".csv,.CSV,text/csv,text/plain" className="hidden" data-testid="quick-import-file-input"
          onChange={(e) => { setFiles([...e.target.files].map((file) => ({ file, status: "ready" }))); e.target.value = ""; }} />
        <button type="button" data-testid="quick-import-pick-btn" disabled={running} onClick={() => ref.current?.click()}
          className="w-full border border-dashed border-[#282D3D] hover:border-[#FFB800] rounded-sm p-4 text-center font-mono text-xs text-slate-400 hover:text-white transition-colors duration-200">
          <FileUp className="w-5 h-5 mx-auto mb-1" />{files.length ? `${files.length} ${t("qi_files")}` : t("qi_pick")}
        </button>
        {files.length > 0 && <ul data-testid="quick-import-files" className="bg-[#090A0F] border border-[#282D3D] rounded-sm max-h-56 overflow-y-auto">{files.map((f, i) => <FileRow key={f.file.name + i} f={f} i={i} />)}</ul>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" data-testid="quick-import-close-btn" disabled={running} onClick={() => onOpenChange(false)} className="rounded-sm bg-transparent border-[#282D3D] hover:bg-[#181B26] hover:text-white">{t("qi_close")}</Button>
          <Button type="button" data-testid="quick-import-run-btn" disabled={!canRun} onClick={run} className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}{t("qi_run")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
